/**
 * AlertTriageBoard - Host-grouped alert triage with split layout
 *
 * Design: incident cards GROUPED BY HOST, split layout with list on left and
 * detail on right. Compact density. Sort: severity then recency. Muted alerts
 * collapse with countdown; acked alerts show acked_by + relative time.
 */

import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button, Input, Alert, Skeleton, SplitView,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  EmptyState, Switch, Label,
} from '@agion/ui'
import {
  AlertTriangle, Search, RefreshCw, ShieldCheck, Info, History,
} from 'lucide-react'
import { PageHeader } from '@/layouts/InfraPageHeader'
import {
  listActiveAlerts, listHosts,
  retryMonitoringRequest, setAlertStatus, clearAlertStatus,
  muteAlert, unmuteAlert, MuteUnavailableError,
  AlertStatusConflictError, MonitoringUnavailableError,
  type AlertStatusAction,
} from './infra_monitoring.service'
import type { MuteDuration } from '@/core/infra_types'
import { FilterShelf, SummaryTile } from './MonitoringPrimitives'
import { HostIncidentCard } from './HostIncidentCard'
import { AlertDetailPanel } from './AlertDetailPanel'
import { groupAlertsByHost, countFiringBySeverity } from './alert_helpers'
import { BellRing } from 'lucide-react'

// Re-export for backwards compatibility
export type { HostIncident } from './alert_helpers'

export function AlertTriageBoard() {
  const { t } = useTranslation(['pages'])
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [severityFilter, setSeverityFilter] = useState<string>('all')
  const [envFilter, setEnvFilter] = useState<string>('all')
  const [showResolved, setShowResolved] = useState(false)
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null)
  const [muteUnavailableReason, setMuteUnavailableReason] = useState<string | null>(null)
  const [statusError, setStatusError] = useState<string | null>(null)

  const { data: alerts = [], isLoading: alertsLoading, isFetching: alertsFetching, error: alertsError, refetch } = useQuery({
    queryKey: ['monitoring', 'alerts', 'active', { includeResolved: showResolved }],
    queryFn: () => listActiveAlerts({ includeResolved: showResolved }),
    staleTime: 30000,
    refetchInterval: 30000,
    retry: retryMonitoringRequest,
  })

  const { data: hosts = [], error: hostsError, refetch: refetchHosts } = useQuery({
    queryKey: ['monitoring', 'hosts'],
    queryFn: listHosts,
    staleTime: 30000,
    retry: retryMonitoringRequest,
  })

  const environments = useMemo(() => {
    return Array.from(new Set(hosts.map(h => h.environmentName))).sort()
  }, [hosts])

  const filtered = useMemo(() => {
    return alerts.filter(alert => {
      const alertName = alert.labels['alertname'] || alert.alertId || ''
      const hostName = hosts.find(h => h.id === alert.hostId)?.name ?? alert.hostId
      const envName = hosts.find(h => h.id === alert.hostId)?.environmentName ?? ''
      const q = searchQuery.toLowerCase()
      const matchesSearch =
        alertName.toLowerCase().includes(q) ||
        hostName.toLowerCase().includes(q)
      const matchesSeverity = severityFilter === 'all' || alert.severity === severityFilter
      const matchesEnv = envFilter === 'all' || envName === envFilter
      return matchesSearch && matchesSeverity && matchesEnv
    })
  }, [alerts, hosts, searchQuery, severityFilter, envFilter])

  const incidents = useMemo(() => groupAlertsByHost(filtered, hosts), [filtered, hosts])

  const selectedAlert = useMemo(() => {
    if (!selectedAlertId) return null
    return alerts.find(a => a.fingerprint === selectedAlertId) ?? null
  }, [alerts, selectedAlertId])

  const summaryCounts = useMemo(() => countFiringBySeverity(alerts), [alerts])

  // One mutation for every operator status (#2357): set = POST, clear = DELETE.
  const statusMutation = useMutation({
    mutationFn: ({ id, status, set }: { id: string; status: AlertStatusAction; set: boolean }) =>
      set ? setAlertStatus(id, status) : clearAlertStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monitoring', 'alerts'] })
      setStatusError(null)
    },
    onError: (error) => {
      if (error instanceof AlertStatusConflictError) {
        setStatusError(t('pages:monitoring.alerts.statusConflict', 'The change was refused: {{reason}}', { reason: error.message }))
      } else if (error instanceof MonitoringUnavailableError) {
        setStatusError(t('pages:monitoring.alerts.statusUnavailable', 'The change could not be saved because the alerts service is unavailable. Try again in a minute.'))
      } else {
        setStatusError(t('pages:monitoring.alerts.statusFailed', 'The change could not be saved: {{reason}}', { reason: error.message }))
      }
    },
  })

  const muteMutation = useMutation({
    mutationFn: ({ id, duration }: { id: string; duration: MuteDuration }) =>
      muteAlert(id, { duration }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monitoring', 'alerts'] })
      setMuteUnavailableReason(null)
    },
    onError: (error) => {
      if (error instanceof MuteUnavailableError) {
        setMuteUnavailableReason(error.message)
      }
    },
  })

  const unmuteMutation = useMutation({
    mutationFn: (id: string) => unmuteAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monitoring', 'alerts'] })
      setMuteUnavailableReason(null)
    },
    onError: (error) => {
      if (error instanceof MuteUnavailableError) {
        setMuteUnavailableReason(error.message)
      }
    },
  })

  const handleSetStatus = useCallback((alertId: string, status: AlertStatusAction) => {
    statusMutation.mutate({ id: alertId, status, set: true })
  }, [statusMutation])

  const handleClearStatus = useCallback((alertId: string, status: AlertStatusAction) => {
    statusMutation.mutate({ id: alertId, status, set: false })
  }, [statusMutation])

  const handleAck = useCallback((alertId: string) => handleSetStatus(alertId, 'ack'), [handleSetStatus])
  const handleUnack = useCallback((alertId: string) => handleClearStatus(alertId, 'ack'), [handleClearStatus])

  const handleMute = useCallback((alertId: string, duration: MuteDuration) => {
    muteMutation.mutate({ id: alertId, duration })
  }, [muteMutation])

  const handleUnmute = useCallback((alertId: string) => {
    unmuteMutation.mutate(alertId)
  }, [unmuteMutation])

  const handleSelectAlert = useCallback((alertId: string) => {
    setSelectedAlertId(prev => prev === alertId ? null : alertId)
  }, [])

  const isMutating = statusMutation.isPending || muteMutation.isPending || unmuteMutation.isPending
  const isLoading = alertsLoading
  const alertsUnavailable = Boolean(alertsError)
  const isUnavailable = alertsUnavailable
  const filtersActive = searchQuery !== '' || severityFilter !== 'all' || envFilter !== 'all'
  const showFilterEmptyState = filtersActive && !isUnavailable

  const listPanel = (
    <div className="h-full flex flex-col">
      {muteUnavailableReason && (
        <Alert variant="warning" className="mx-4 mt-4 mb-0">
          {muteUnavailableReason}
        </Alert>
      )}
      {statusError && (
        <Alert variant="warning" className="mx-4 mt-4 mb-0">
          {statusError}
        </Alert>
      )}

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {isLoading ? (
          <LoadingSkeleton />
        ) : incidents.length === 0 ? (
          <EmptyState
            icon={showFilterEmptyState ? Search : isUnavailable ? Info : ShieldCheck}
            title={
              showFilterEmptyState
                ? t('pages:monitoring.alerts.noResultsTitle', 'No matching alerts')
                : isUnavailable
                  ? t('pages:monitoring.alerts.backendPendingTitle', 'Alert data unavailable')
                  : t('pages:monitoring.alerts.emptyTitle', 'All systems operational')
            }
            description={
              showFilterEmptyState
                ? t('pages:monitoring.alerts.noResults', 'No alerts match your filters.')
                : isUnavailable
                  ? t('pages:monitoring.alerts.backendPending', 'Active alerts cannot be confirmed until monitoring backend is available.')
                  : t('pages:monitoring.alerts.empty', 'No active alerts. All systems operational.')
            }
            action={
              showFilterEmptyState ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearchQuery('')
                    setSeverityFilter('all')
                    setEnvFilter('all')
                  }}
                >
                  {t('pages:monitoring.alerts.clearFilters', 'Clear filters')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          incidents.map(incident => (
            <HostIncidentCard
              key={incident.hostId}
              incident={incident}
              selectedAlertId={selectedAlertId}
              onSelectAlert={handleSelectAlert}
              onAck={handleAck}
              onUnack={handleUnack}
              onMute={handleMute}
              onUnmute={handleUnmute}
              isMutating={isMutating}
            />
          ))
        )}
      </div>
    </div>
  )

  const detailPanel = selectedAlert ? (
    <AlertDetailPanel
      alert={selectedAlert}
      host={hosts.find(h => h.id === selectedAlert.hostId) ?? null}
      onClose={() => setSelectedAlertId(null)}
      onSetStatus={handleSetStatus}
      onClearStatus={handleClearStatus}
      onMute={handleMute}
      onUnmute={handleUnmute}
      isMutating={isMutating}
    />
  ) : null

  return (
    <div className="container-standard flex flex-col h-[calc(100vh-4rem)]">
      <PageHeader
        icon={AlertTriangle}
        iconColor="text-rose-600"
        title={t('pages:monitoring.alerts.title', 'Active Alerts')}
        description={t('pages:monitoring.alerts.description', 'Monitor active alerts across all edge hosts')}
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { void refetch(); void refetchHosts() }}
            disabled={alertsFetching}
            aria-label={t('pages:monitoring.alerts.refresh', 'Refresh alerts')}
            className="flex items-center gap-2"
          >
            <RefreshCw size={14} className={alertsFetching ? 'animate-spin' : ''} />
            {t('pages:monitoring.alerts.refresh', 'Refresh')}
          </Button>
        }
      />

      {isUnavailable && (
        <Alert variant="info" className="mb-4">
          {t('pages:monitoring.alerts.unavailable', 'Unable to refresh alerts. Any displayed alerts are the last loaded results.')}
        </Alert>
      )}

      {Boolean(hostsError) && (
        <Alert variant="info" className="mb-4">{t('pages:monitoring.alerts.hostContextUnavailable', 'Host context unavailable; alert results are still shown.')}</Alert>
      )}

      <div className="mb-4 grid gap-4 sm:grid-cols-3">
        <SummaryTile
          label={t('pages:monitoring.alerts.critical', 'Critical')}
          value={isLoading ? <Skeleton className="h-8 w-12" /> : alertsUnavailable ? '-' : summaryCounts.critical}
          valueTestId="critical-count"
          tone="critical"
          icon={BellRing}
        />
        <SummaryTile
          label={t('pages:monitoring.alerts.warning', 'Warning')}
          value={isLoading ? <Skeleton className="h-8 w-12" /> : alertsUnavailable ? '-' : summaryCounts.warning}
          valueTestId="warning-count"
          tone="warning"
          icon={AlertTriangle}
        />
        <SummaryTile
          label={t('pages:monitoring.alerts.info', 'Info')}
          value={isLoading ? <Skeleton className="h-8 w-12" /> : alertsUnavailable ? '-' : summaryCounts.info}
          valueTestId="info-count"
          tone="info"
          icon={Info}
        />
      </div>

      <FilterShelf>
        <div className="relative min-w-0 flex-1">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input
            placeholder={t('pages:monitoring.alerts.search', 'Search alerts...')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger className="w-44" aria-label={t('pages:monitoring.alerts.filters.severity', 'Filter by severity')}>
            <SelectValue placeholder={t('pages:monitoring.alerts.filters.allSeverities', 'All Severities')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('pages:monitoring.alerts.filters.allSeverities', 'All Severities')}</SelectItem>
            <SelectItem value="critical">{t('pages:monitoring.alerts.severity.critical', 'Critical')}</SelectItem>
            <SelectItem value="warning">{t('pages:monitoring.alerts.severity.warning', 'Warning')}</SelectItem>
            <SelectItem value="info">{t('pages:monitoring.alerts.severity.info', 'Info')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={envFilter} onValueChange={setEnvFilter}>
          <SelectTrigger className="w-52" aria-label={t('pages:monitoring.alerts.filters.environment', 'Filter by environment')}>
            <SelectValue placeholder={t('pages:monitoring.alerts.filters.allEnvironments', 'All Environments')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('pages:monitoring.alerts.filters.allEnvironments', 'All Environments')}</SelectItem>
            {environments.map(env => (
              <SelectItem key={env} value={env}>{env}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Switch
            id="show-resolved"
            checked={showResolved}
            onCheckedChange={setShowResolved}
            aria-label={t('pages:monitoring.alerts.showResolved', 'Show resolved alerts')}
          />
          <Label htmlFor="show-resolved" className="text-sm font-normal text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
            <History size={14} />
            {t('pages:monitoring.alerts.history24h', '24h history')}
          </Label>
        </div>
      </FilterShelf>

      <div className="flex-1 min-h-0 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900">
        <SplitView
          left={listPanel}
          right={detailPanel}
          defaultSplit={45}
          minLeft={320}
          minRight={380}
          storageKey="alert-triage-split"
        />
      </div>
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map(i => (
        <div key={i} className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-3 mb-3">
            <Skeleton className="h-5 w-5 rounded-full" />
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-4 w-24 ml-auto" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-12 w-full rounded" />
            <Skeleton className="h-12 w-full rounded" />
          </div>
        </div>
      ))}
    </div>
  )
}
